import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from 'styled-components'
import Particles, { initParticlesEngine } from '@tsparticles/react'
import { loadSlim } from '@tsparticles/slim'
import { Content, Root } from './Jumbotron.styles'

/**
 * The Jumbotron's component
 */
const Jumbotron = ({ children, color, height, linksColor, pointsColor, ...restProps }) => {
  const theme = useTheme()
  const [init, setInit] = useState(false)

  useEffect(() => {
    initParticlesEngine(async engine => {
      await loadSlim(engine)
    })
      .then(() => setInit(true))
      .catch(error => {
        console.error('Error initializing particles engine:', error)
      })
  }, [])

  const options = useMemo(
    () => ({
      detectRetina: true,
      fpsLimit: 60,
      interactivity: {
        events: {
          onHover: { enable: true, mode: 'repulse' }
        },
        modes: {
          push: { quantity: 4 },
          repulse: { distance: 200, duration: 0.4 }
        }
      },
      particles: {
        color: { value: pointsColor || theme.palette.brand.gray1 },
        links: {
          color: linksColor || theme.palette.brand.gray1,
          distance: 150,
          enable: true,
          opacity: 0.5,
          width: 1
        },
        move: {
          direction: 'none',
          enable: true,
          outModes: { default: 'bounce' },
          random: false,
          speed: 1.5,
          straight: false
        },
        number: {
          density: { enable: true, area: 800 },
          value: 80
        },
        opacity: { value: 0.5 },
        shape: { type: 'circle' },
        size: { value: { min: 1, max: 5 } }
      },
      fullScreen: { enable: false, zIndex: 0 }
    }),
    [linksColor, pointsColor, theme.palette.brand.gray1]
  )

  return (
    <Root {...restProps} color={color} height={height}>
      {init && <Particles id="jumbotron" options={options} />}
      <Content>{children}</Content>
    </Root>
  )
}

Jumbotron.defaultProps = {
  color: 'gray',
  height: '213px',
  linksColor: undefined,
  pointsColor: undefined
}

Jumbotron.propTypes = {
  color: PropTypes.oneOf(['gray', 'blue']),
  children: PropTypes.node.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  linksColor: PropTypes.string,
  pointsColor: PropTypes.string
}

export default Jumbotron
